// CVC2: the pure fold behind the conversation's Canvas card. A neucleos_canvas
// create or save answers {canvasId, memberId, revisionId, sequence}; anything
// else (a catalog, a read, a foreign tool) folds to null. Pure so the row
// builder can lift a creation onto a folded turn without rendering anything.
export interface CanvasToolCardModel {
  readonly canvasId: string;
  readonly memberId: string;
  /** Exact authority result, never reconstructed from the current head. */
  readonly revisionId: string;
  readonly sequence: number;
  readonly title: string | null;
  readonly action: "create" | "save" | null;
}
const CANVAS_ID = /^cnv_[0-9a-f]{24}$/;
/**
 * Can 47 (Jason, 19 Oct 2026): the Pocket tool list, read like a Canvas at this id and built by the World from its own catalog at
 * each read (backend lib/pocketToolList.js POCKET_TOOLS_PAGE). Its one revision is named by the same id, at sequence 1.
 */
export const POCKET_TOOLS_CANVAS_ID = "pocket-tools";
export const POCKET_TOOLS_TITLE = "What Cleo can do from Pocket";
const SCAN_DEPTH = 6;
const MAX_SAVED_RESULT_TEXT = 16_384;
function isCanvasToolName(name: string): boolean {
  if (name === "neucleos_canvas") return true;
  const parts = name.split("__");
  return (
    parts.length === 3 && parts[0] === "mcp" && Boolean(parts[1]) && parts[2] === "neucleos_canvas"
  );
}
const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Codex retains an item envelope; Claude and AI SDK retain the flat call. */
export function mcpToolActivityData(data: unknown): unknown {
  const record = asRecord(data);
  return record?.["item"] !== undefined ? record["item"] : (record ?? undefined);
}

function savedResult(value: unknown) {
  const record = asRecord(value);
  if (
    !record ||
    typeof record["canvasId"] !== "string" ||
    !CANVAS_ID.test(record["canvasId"]) ||
    typeof record["revisionId"] !== "string" ||
    record["revisionId"].length === 0 ||
    typeof record["sequence"] !== "number" ||
    !Number.isSafeInteger(record["sequence"]) ||
    record["sequence"] < 1
  )
    return null;
  return {
    canvasId: record["canvasId"],
    memberId:
      typeof record["memberId"] === "string"
        ? record["memberId"]
        : `world-vfs:canvas/${record["canvasId"]}`,
    revisionId: record["revisionId"],
    sequence: record["sequence"],
  };
}

/** The AI SDK's successful World result is one JSON line followed by an
 * optional approval receipt. Never search prose or nested read/catalog data. */
function savedTextResult(record: Record<string, unknown>) {
  if (record["toolName"] !== "neucleos_canvas") return null;
  const output = asRecord(record["output"]);
  if (
    output?.["isError"] !== false ||
    typeof output["text"] !== "string" ||
    output["text"].length > MAX_SAVED_RESULT_TEXT
  )
    return null;
  try {
    return savedResult(JSON.parse(output["text"].split("\n", 1)[0]!));
  } catch {
    return null;
  }
}

function findSaveResult(
  value: unknown,
  depth: number,
): { canvasId: string; memberId: string; revisionId: string; sequence: number } | null {
  if (depth < 0 || value === null || typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = findSaveResult(entry, depth - 1);
      if (found) return found;
    }
    return null;
  }
  const record = value as Record<string, unknown>;
  const saved = savedResult(record);
  if (saved) return saved;
  for (const entry of Object.values(record)) {
    const found = findSaveResult(entry, depth - 1);
    if (found) return found;
  }
  return null;
}
function findInput(value: unknown, depth: number): { title: string | null; action: string } | null {
  if (depth < 0 || value === null || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const action = record["action"];
  if (typeof action === "string")
    return { title: typeof record["title"] === "string" ? record["title"] : null, action };
  for (const entry of Object.values(record)) {
    const found = findInput(entry, depth - 1);
    if (found) return found;
  }
  return null;
}

/**
 * Can 47: Pocket's card for Cleo's read of the Pocket tool list (a neucleos_canvas read at POCKET_TOOLS_CANVAS_ID that the World
 * answered), which opens the list in Pocket's Canvas viewer from the World (canvas:read). Null for any other call. Pocket's own: the
 * Desk opens Canvases another way and is not taught this read.
 */
export function pocketToolsCardModel(
  toolName: string,
  input: unknown,
  answered: boolean,
): CanvasToolCardModel | null {
  const call = asRecord(input);
  if (!answered || !isCanvasToolName(toolName) || call?.["action"] !== "read" || call["canvasId"] !== POCKET_TOOLS_CANVAS_ID)
    return null;
  return {
    canvasId: POCKET_TOOLS_CANVAS_ID,
    memberId: `world-vfs:canvas/${POCKET_TOOLS_CANVAS_ID}`,
    revisionId: POCKET_TOOLS_CANVAS_ID,
    sequence: 1,
    title: POCKET_TOOLS_TITLE,
    action: null,
  };
}

/** The pure fold the Work Log row and the turn fold ask: a card model, or null. */
export function canvasToolCardModel(
  label: string | undefined,
  toolData: unknown,
): CanvasToolCardModel | null {
  if (toolData === undefined) return null;
  if (!(label ?? "").toLowerCase().includes("canvas")) return null;
  const record = asRecord(toolData);
  if (
    record &&
    ((typeof record["toolName"] === "string" && !isCanvasToolName(record["toolName"])) ||
      record["isError"] === true ||
      asRecord(record["output"])?.["isError"] === true ||
      asRecord(record["result"])?.["isError"] === true)
  )
    return null;
  const input = findInput(toolData, SCAN_DEPTH);
  if (input && input.action !== "create" && input.action !== "save") return null;
  const saved =
    record?.["output"] !== undefined
      ? savedTextResult(record)
      : findSaveResult(toolData, SCAN_DEPTH);
  if (!saved) return null;
  return {
    ...saved,
    title: input?.title ?? null,
    action: input?.action === "create" || input?.action === "save" ? input.action : null,
  };
}
